import { Names } from "@/components/Cashier/OptionsAndAddonAccordion";
import { BrowserDetectionResult, CouponType } from "@/types/globalTypes";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { messaging } from "@/firebase/firebaseConfig";
import axios from "axios";
import { NavigateFunction } from "react-router-dom";
import { Browser } from '@capacitor/browser';
import { DateTimeFormatOptions } from "@/components/Cashier/CashierHistoryBody";
import { InAppReview } from '@capacitor-community/in-app-review';


export const googleMapsKey = Capacitor.getPlatform() === "web" ? import.meta.env.VITE_GOOGLE_KEY : import.meta.env.VITE_GOOGLE_KEY_MOBILE

export const isProduction = import.meta.env.VITE_SENTRY_ENVIRONMENT === "production";
export const isWeb = Capacitor.getPlatform() === 'web';


export function formatDateForInput(unixTimestamp?: number | null): string {
  if (!unixTimestamp || isNaN(unixTimestamp)) return "";

  // Automatically detect and adjust milliseconds
  const adjustedTimestamp =
    unixTimestamp > 9999999999
      ? Math.floor(unixTimestamp / 1000)
      : unixTimestamp;

  const date = new Date(adjustedTimestamp * 1000);

  // Validate if the resulting date is valid
  if (isNaN(date.getTime())) {
    console.error("Invalid timestamp:", unixTimestamp);
    return "";
  }

  // Format the date as 'YYYY-MM-DD' for input
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function timeAgo(addedTime: number, lang: string): string {
  const now = Date.now();
  const seconds = Math.floor((now - addedTime) / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30); // Approximation
  const years = Math.floor(months / 12);

  const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });

  if (seconds < 60) {
    return rtf.format(-seconds, "second");
  } else if (minutes < 60) {
    return rtf.format(-minutes, "minute");
  } else if (hours < 24) {
    return rtf.format(-hours, "hour");
  } else if (days < 30) {
    return rtf.format(-days, "day");
  } else if (months < 12) {
    return rtf.format(-months, "month");
  } else {
    return rtf.format(-years, "year");
  }
}

export function isEmptyHTML(html: string): boolean {
  const div = document.createElement("div");
  div.innerHTML = html.trim();

  const stack: Node[] = [...div.childNodes];

  while (stack.length > 0) {
    const node = stack.pop()!;

    if (node.nodeType === Node.TEXT_NODE && node.nodeValue?.trim()) {
      return false;
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      stack.push(...node.childNodes);
    }
  }

  return true;
}



export const getMaxImageSize = (sizeInMB: number) => {
  const MAX_IMAGE_SIZE = sizeInMB * 1024 * 1024;
  const MAX_IMAGE_SIZE_MB_String = {
    en: `${sizeInMB}MB`,
    ar: `${sizeInMB} ميجابايت`,
  };

  return { MAX_IMAGE_SIZE, MAX_IMAGE_SIZE_MB_String };
};

export const getMaxFileSize = (sizeInMB: number) => {
  const MAX_FILE_SIZE = sizeInMB * 1024 * 1024;
  const MAX_FILE_SIZE_MB_String = {
    en: `${sizeInMB}MB`,
    ar: `${sizeInMB} ميجابايت`,
  };

  return { MAX_FILE_SIZE, MAX_FILE_SIZE_MB_String };
};

export function couponeDiscount(subtotal: number, couponType: CouponType, amount?: number): number {
  if (!couponType || amount === undefined || amount <= 0) {
    return subtotal;
  }

  let discount = 0;

  if (couponType === "percentage") {
    discount = (subtotal * amount) / 100;
  } else if (couponType === "fixed") {
    discount = amount;
  }

  const total = Math.max(subtotal - discount, 0);
  return total;
}

export const flattenOptions = (options: Names[][]): Names[] => {
  return (options || []) // Ensure options is an array
    .flat()
    .filter(Boolean) // Remove null or undefined values
    .map(({ nameAR, nameEN }) => ({ nameAR, nameEN }));
};

export async function clearServiceWorkers(userToken: string) {
  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const registerDeviceUrl = `${BASE_URL}/api/dashboard/fcm`;

  try {
    const token = await messaging?.getToken({
      vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
    });

    if (token) {
      await axios.delete(registerDeviceUrl, {
        headers: {
          Authorization: userToken,
          "Content-Type": "application/json",
        },
        data: { token },
      });
    }
  } catch (error) {
    console.error("Failed to remove FCM token:", error);
  }

  if (Capacitor.getPlatform() === "web") {

    try {
      if (!('serviceWorker' in navigator)) {
        console.warn('Service Workers are not supported in this browser.');
        return;
      }

      const registrations = await navigator.serviceWorker.getRegistrations();

      if (!registrations || registrations.length === 0) {
        console.info('No Service Workers found to unregister.');
        return;
      }

      for (const registration of registrations) {
        try {
          const success = await registration.unregister();
          if (success) {
            console.info(`Service Worker unregistered: ${registration.scope}`);
          } else {
            console.warn(`Failed to unregister Service Worker: ${registration.scope}`);
          }
        } catch (innerErr) {
          console.error('Error during individual Service Worker unregistration:', innerErr);
        }
      }
    } catch (err) {
      console.error('Unexpected error while clearing Service Workers:', err);
    }
  } else {

    await PushNotifications.unregister();

  }
}


export const printFromUrl = (url: string) => {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none";
  iframe.src = url;

  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    }, 1000); // Adjust delay if needed
  };

  document.body.appendChild(iframe);
};

export async function detectBrowser(): Promise<BrowserDetectionResult> {
  try {
    const ua = navigator.userAgent.toLowerCase();

    // Step 1: Detect in-app browsers 
    if (ua.includes("snapchat")) return { name: "snapchat", type: "in-app" };
    if (ua.includes("instagram")) return { name: "instagram", type: "in-app" };
    if (ua.includes("fbav") || ua.includes("fban")) return { name: "facebook", type: "in-app" };
    if (ua.includes("tiktok")) return { name: "tiktok", type: "in-app" };
    if (ua.includes("twitter")) return { name: "twitter", type: "in-app" };
    if (ua.includes("pinterest")) return { name: "pinterest", type: "in-app" };
    if (ua.includes("linkedin")) return { name: "linkedin", type: "in-app" };

    // Step 2: Detect standalone 
    if (/edgios/.test(ua) || /edg\//.test(ua)) return { name: "edge", type: "standalone" };
    if (/opr\//.test(ua) || ua.includes("opera")) return { name: "opera", type: "standalone" };
    if (/firefox/.test(ua) || /fxios/.test(ua)) return { name: "firefox", type: "standalone" };
    if (/crios/.test(ua) || (/chrome/.test(ua) && !/edge|edg\//.test(ua))) return { name: "chrome", type: "standalone" };
    if (/safari/.test(ua) && !/chrome|crios/.test(ua)) return { name: "safari", type: "standalone" };

    // Default fallback
    return { name: "other", type: "unknown" };
  } catch (error) {
    console.error("Browser detection failed:", error);
    return { name: "other", type: "unknown" };
  }
}

// utils/formatMoney.ts
export function formatMoney(value: number | string): string {
  // Handle null, undefined, or empty string
  if (value === null || value === undefined || value === '') {
    return '0.00';
  }

  // Convert string to number explicitly, clean unwanted characters
  let num: number;

  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.-]+/g, ''); // Remove non-numeric characters (except dot and minus)
    num = Number(cleaned);
  } else {
    num = value;
  }

  // If not a valid number, return "0.00"
  if (isNaN(num)) {
    return '0.00';
  }

  // Format with thousand separator and 2 decimal places
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}





export function formatMoneyForInput(value: number | string | undefined): string {
  // Handle null, undefined, empty string
  if (value === null || value === undefined || value === '') {
    return "";
  }

  // Convert to number safely
  const num = typeof value === 'number' ? value : Number(value.toString().trim());

  // If not a valid number, return "0.00"
  if (isNaN(num)) {
    return '0.00';
  }

  return num.toFixed(2); // Always 2 decimal places
}

export const formatDateTime = ({ timestamp }: DateTimeFormatOptions) => {
  const date = new Date(timestamp * 1000);
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  const formattedEnglish = date.toLocaleString("en-US", options);
  const formattedArabicRaw = date.toLocaleString("ar-EG", options);
  const arabicNumbers = "٠١٢٣٤٥٦٧٨٩";
  const englishNumbers = "0123456789";

  const formattedArabic = formattedArabicRaw.replace(
    /[٠-٩]/g,
    (d) => englishNumbers[arabicNumbers.indexOf(d)],
  );

  return { formattedEnglish, formattedArabic };
};


// Moyaser Function
function hexToArrayBuffer(hex: string): ArrayBuffer {
  if (hex.length % 2 !== 0) {
    throw new Error("Hex string must have an even length");
  }

  const length = hex.length / 2;
  const buffer = new ArrayBuffer(length);
  const view = new Uint8Array(buffer);

  for (let i = 0; i < length; i++) {
    view[i] = parseInt(hex.substr(i * 2, 2), 16);
  }

  return buffer;
}

export async function decryptPaymentData(paymentData: string) {
  const keyData = hexToArrayBuffer(
    import.meta.env.VITE_MOYASAR_ENCRYPTION_KEY as string
  );

  const key = await crypto.subtle.importKey("raw", keyData, "AES-CTR", false, [
    "decrypt",
  ]);
  const decryptedData = await crypto.subtle.decrypt(
    {
      name: "AES-CTR",
      length: 128,
      counter: new Uint8Array([0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0]),
    },
    key,
    new Uint8Array([...atob(paymentData)].map((x) => x.charCodeAt(0)))
  );
  const decoder = new TextDecoder();
  return JSON.parse(decoder.decode(decryptedData));
}


export const navigateToSubscribtion = async (navigate: NavigateFunction, token: string) => {
  if (Capacitor.getPlatform() === "web") {
    navigate("/settings/subscribtion");
  } else {
    const environment = import.meta.env.VITE_ENVIRONMENT
    if (environment === "production") {
      await Browser.open({ url: `https://app.interflow.sa/login?token=${token}&subscription-redirect=true` });
    } else {
      await Browser.open({ url: `https://dev.interflow.sa/login?token=${token}&subscription-redirect=true` });
    }
  }

}


export function getDaysLeft(timestamp: number): number {
    const now = new Date();
    const expireDate = new Date(timestamp * 1000);
    const diffTime = expireDate.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }


  export const mobileAppReview = async () => {
  if(Capacitor.getPlatform() === "web") return
  await InAppReview.requestReview()
    .then(() => {
      console.log("reviewed successfully");
    })
    .catch((error) => console.error("error", error));
};